import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { mandantenAPI, reportsAPI } from '../lib/api';
import MarkBezahltModal from '../components/MarkBezahltModal';

function fmtMoney(v) {
  const n = Number(v || 0);
  if (n === 0) return '';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
}

function fmtDate(s) {
  if (!s) return '—';
  return format(new Date(s), 'dd.MM.yyyy');
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function ageBadge(days) {
  if (days == null) return null;
  if (days < 0) return <span className="muted small">in {-days} T</span>;
  let color = '#475569';
  if (days > 60) color = '#991b1b';
  else if (days > 30) color = '#854d0e';
  return <span className="small" style={{ color }}>{days} T</span>;
}

function GroupTable({ groups, quelle, onMarkPaid, partnerLabel }) {
  if (groups.length === 0) {
    return <div className="card empty">Keine offenen Posten im Zeitraum.</div>;
  }

  const total = groups.reduce((acc, g) => {
    acc.offen += Number(g.offen_summe);
    acc.brutto += Number(g.brutto_summe);
    acc.bezahlt += Number(g.bezahlt_summe);
    acc.anzahl += g.anzahl;
    return acc;
  }, { offen: 0, brutto: 0, bezahlt: 0, anzahl: 0 });

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div className="muted small">{partnerLabel}</div>
            <div style={{ fontWeight: 600 }}>{groups.length}</div>
          </div>
          <div>
            <div className="muted small">Posten</div>
            <div style={{ fontWeight: 600 }}>{total.anzahl}</div>
          </div>
          <div>
            <div className="muted small">Brutto gesamt</div>
            <div style={{ fontWeight: 600 }}>{fmtMoney(total.brutto)}</div>
          </div>
          <div>
            <div className="muted small">Bereits bezahlt</div>
            <div style={{ fontWeight: 600 }}>{fmtMoney(total.bezahlt)}</div>
          </div>
          <div>
            <div className="muted small">Offen</div>
            <div style={{ fontWeight: 700, color: total.offen > 0 ? '#991b1b' : '#166534' }}>
              {fmtMoney(total.offen)}
            </div>
          </div>
        </div>
      </div>

      {groups.map((g) => (
        <div key={g.partner_id} className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <h3 style={{ margin: 0 }}>{g.partner_name}</h3>
            <div className="muted small">
              {g.anzahl} Posten · Offen <b style={{ color: Number(g.offen_summe) > 0 ? '#991b1b' : '#166534' }}>{fmtMoney(g.offen_summe)}</b>
            </div>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Datum</th>
                <th>Rechn.-Nr</th>
                <th className="num">Brutto</th>
                <th className="num">Bezahlt</th>
                <th className="num">Offen</th>
                <th>Bez. am</th>
                <th>Alter</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {g.items.map((it) => (
                <tr key={`${it.quelle}-${it.id}`}>
                  <td>{fmtDate(it.beleg_datum)}</td>
                  <td>{it.rechnungsnummer || '—'}</td>
                  <td className="num">{fmtMoney(it.betrag_brutto)}</td>
                  <td className="num">{fmtMoney(it.bezahlt_betrag)}</td>
                  <td className="num">
                    <b style={{ color: Number(it.offen) > 0.005 ? '#991b1b' : '#166534' }}>
                      {fmtMoney(it.offen)}
                    </b>
                  </td>
                  <td>{fmtDate(it.bezahlt_am)}</td>
                  <td>{ageBadge(it.tage_alt)}</td>
                  <td>
                    <button
                      className="btn-text"
                      onClick={() => onMarkPaid({
                        id: it.id,
                        betrag_brutto: it.betrag_brutto,
                        bezahlt_am: it.bezahlt_am,
                        bezahlt_betrag: it.bezahlt_betrag,
                        quelle,
                      })}
                    >
                      {it.bezahlt_am ? 'Bearbeiten' : 'Bezahlt'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </>
  );
}

export default function Opl() {
  const { slug } = useParams();
  const [tab, setTab] = useState('lieferanten'); // 'lieferanten' | 'kunden'
  const [stichtag, setStichtag] = useState(todayISO());
  const [includeBezahlt, setIncludeBezahlt] = useState(false);
  const [mandant, setMandant] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalRow, setModalRow] = useState(null);

  useEffect(() => {
    mandantenAPI.get(slug).then((r) => setMandant(r.data)).catch(() => {});
  }, [slug]);

  const reload = useCallback(() => {
    setLoading(true);
    const fn = tab === 'kunden' ? reportsAPI.oplKunden : reportsAPI.oplLieferanten;
    return fn(slug, { stichtag, include_bezahlt: includeBezahlt })
      .then((r) => setGroups(r.data))
      .catch((err) => toast.error(err.response?.data?.detail || 'OPL konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [slug, stichtag, includeBezahlt, tab]);

  useEffect(() => { reload(); }, [reload]);

  const quelle = tab === 'kunden' ? 'ausgang' : 'eingang';
  const partnerLabel = tab === 'kunden' ? 'Kunden' : 'Lieferanten';

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Offene Posten</h1>
          {mandant && (
            <div className="muted">{mandant.name} · {mandant.skr_variant}</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          <Link to={`/mandanten/${slug}/reports`} className="btn-text">← SuSa</Link>
          <Link to={`/mandanten/${slug}/reports/ustva`} className="btn-text">UStVA →</Link>
        </div>
      </header>

      <div className="topnav-tabs" style={{ marginBottom: 16 }}>
        <button
          className={`tab ${tab === 'lieferanten' ? 'active' : ''}`}
          onClick={() => setTab('lieferanten')}
        >
          Lieferanten (Kreditoren)
        </button>
        <button
          className={`tab ${tab === 'kunden' ? 'active' : ''}`}
          onClick={() => setTab('kunden')}
        >
          Kunden (Debitoren)
        </button>
      </div>

      <div className="filters" style={{ flexWrap: 'wrap' }}>
        <label>
          Stichtag: <input
            type="date"
            value={stichtag}
            onChange={(e) => setStichtag(e.target.value)}
          />
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={includeBezahlt}
            onChange={(e) => setIncludeBezahlt(e.target.checked)}
            style={{ width: 'auto' }}
          />
          Bezahlte einbeziehen
        </label>
        <button className="btn-text" onClick={reload} disabled={loading}>
          {loading ? 'Lädt…' : 'Neu laden'}
        </button>
      </div>

      {loading ? (
        <div className="muted">Lädt…</div>
      ) : (
        <GroupTable
          groups={groups}
          quelle={quelle}
          partnerLabel={partnerLabel}
          onMarkPaid={(row) => setModalRow(row)}
        />
      )}

      <MarkBezahltModal
        open={!!modalRow}
        onClose={() => setModalRow(null)}
        slug={slug}
        quelle={modalRow?.quelle}
        row={modalRow}
        onSaved={() => reload()}
      />
    </div>
  );
}
